import React, { useState, useRef, useCallback } from 'react';
import proj4 from 'proj4';
import CustomAutocomplete from './CustomAutocomplete';
import apiConfig from '../config/api';
import './ChatAssistant.css';

// EPSG:2039 (Israeli ITM) — same definition as src/components/LocationMap.js.
proj4.defs('EPSG:2039', '+proj=tmerc +lat_0=31.73439361111111 +lon_0=35.20451694444445 +k=1.0000067 +x_0=219529.584 +y_0=626907.39 +ellps=GRS80 +towgs84=-48,55,52,0,0,0,0 +units=m +no_defs');
proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');

const RECENT_KEY = 'kb-chat-recent';
const RECENT_MAX = 10;
const DOCUMENT_OPTIONS = [
  { id: '', title: 'מאגר תכנון עירוני' },
  { id: 'nesach-3', title: 'נסח 3' },
];

function loadRecent() {
  try { return JSON.parse(sessionStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}
function saveRecent(items) {
  try { sessionStorage.setItem(RECENT_KEY, JSON.stringify(items.slice(0, RECENT_MAX))); } catch { /* ignore */ }
}

function generateConversationId() {
  return 'conv_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

// Render answer text with minimal Markdown handling: **bold**, *italic*,
// `code`, and `### heading` lines. Defensive — the system prompt forbids
// Markdown, but models leak it; rather than show literal `**foo**` we
// either parse it or strip it. Keep this small (no markdown lib dep).
function renderRichText(text) {
  if (!text) return null;
  // Drop heading hashes at line start ("### Foo" → "Foo")
  const cleaned = text.replace(/^#{1,6}\s+/gm, '');
  const parts = [];
  // Split on **bold**, *italic*, and `code` in one pass.
  const re = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g;
  let lastIndex = 0;
  let m;
  let key = 0;
  while ((m = re.exec(cleaned)) !== null) {
    if (m.index > lastIndex) parts.push(cleaned.slice(lastIndex, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) parts.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) parts.push(<code key={key++}>{tok.slice(1, -1)}</code>);
    else parts.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    lastIndex = m.index + tok.length;
  }
  if (lastIndex < cleaned.length) parts.push(cleaned.slice(lastIndex));
  return parts;
}

function getCitationKind(citation) {
  if (citation?.source_type) return citation.source_type;
  if (String(citation?.id || '').startsWith('plan-bbox:')) return 'plan_metadata';
  if (String(citation?.id || '').startsWith('chunk:')) return 'text_excerpt';
  if (citation?.spatial_layer) return 'spatial_layer';
  if (citation?.excerpt) return 'summary';
  return 'reference';
}

function getCitationKindLabel(kind) {
  switch (kind) {
    case 'text_excerpt': return 'קטע מקור';
    case 'extracted_rules': return 'נתון שחולץ';
    case 'plan_metadata': return 'מטא-דאטה';
    case 'spatial_layer': return 'שכבה מרחבית';
    case 'permit': return 'היתר';
    case 'uploaded_document': return 'מסמך בדיקה';
    default: return 'מקור';
  }
}

function formatCitationTitle(citation) {
  const parts = [];
  if (citation?.plan_ref) parts.push(citation.plan_ref);
  if (citation?.title) parts.push(citation.title);
  if (citation?.title && citation.title !== citation.plan_ref) parts.push(citation.title);
  if (citation?.spatial_layer) parts.push(citation.spatial_layer);
  return Array.from(new Set(parts)).join(' · ') || 'מקור';
}

function formatCitationPages(citation) {
  if (citation?.page_num) return `עמ' ${citation.page_num}`;
  if (Array.isArray(citation?.page_numbers) && citation.page_numbers.length > 0) {
    return `עמ' ${citation.page_numbers.join(', ')}`;
  }
  return '';
}

function formatCitationBody(citation) {
  return String(citation?.excerpt || '').trim();
}

function formatCitationNote(citation) {
  const note = String(citation?.note || '').trim();
  if (!note || note === String(citation?.excerpt || '').trim()) return '';
  return note;
}

export default function ChatAssistant() {
  const [addressResolved, setAddressResolved] = useState(null);
  const [question, setQuestion] = useState('');
  const [streamingText, setStreamingText] = useState('');
  const [thinkingTrail, setThinkingTrail] = useState([]);
  const [citations, setCitations] = useState([]);
  const [toolsUsed, setToolsUsed] = useState([]);
  const [confidence, setConfidence] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [chatQueryId, setChatQueryId] = useState(null);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [recent, setRecent] = useState(() => loadRecent());
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const conversationIdRef = useRef(generateConversationId());
  const selectedDocument = DOCUMENT_OPTIONS.find(d => d.id === selectedDocumentId && d.id) || null;

  const handleAddressSelected = useCallback(async (placeResult) => {
    // CustomAutocomplete's onPlaceSelected returns
    //   { formatted_address, geometry: { location }, name }
    // where location is a google maps LatLng-shaped object (new API uses
    // accessor methods .lat()/.lng(); legacy fallback exposes plain props).
    const loc = placeResult?.geometry?.location;
    if (!loc) return;
    const lat = typeof loc.lat === 'function' ? loc.lat() : (loc.lat ?? loc.latitude);
    const lng = typeof loc.lng === 'function' ? loc.lng() : (loc.lng ?? loc.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    try {
      // WGS84 → ITM (EPSG:2039) before calling govmap-layers.
      const [x_itm, y_itm] = proj4('EPSG:4326', 'EPSG:2039', [Number(lng), Number(lat)]);
      const resp = await fetch(`${apiConfig.apiBaseUrl}/api/govmap-layers?x=${x_itm}&y=${y_itm}`);
      const data = await resp.json();
      // /api/govmap-layers returns processGovMapResponse output with Hebrew
      // field names from layer 'parcel_ownership_new': ownership[0]['גוש'],
      // ownership[0]['חלקה']. (Earlier code looked for data.parcel.gush
      // which doesn't exist — left gush/helka null and starved the LLM.)
      const ownership = Array.isArray(data?.ownership) && data.ownership.length > 0 ? data.ownership[0] : null;
      const gush = ownership?.['גוש'] != null ? String(ownership['גוש']) : null;
      const helka = ownership?.['חלקה'] != null ? String(ownership['חלקה']) : null;
      setAddressResolved({
        text: placeResult.formatted_address || placeResult.name,
        x_itm,
        y_itm,
        gush,
        helka,
        parcel_key: gush && helka ? `${gush}-${helka}` : null,
      });
    } catch (e) {
      setError('כתובת לא ניתנת לפתרון. נסה שוב.');
    }
  }, []);

  const handleAsk = useCallback(async () => {
    if (!question.trim() || isStreaming) return;
    setError(null);
    setStreamingText('');
    setThinkingTrail([]);
    setCitations([]);
    setToolsUsed([]);
    setConfidence(null);
    setChatQueryId(null);
    setFeedbackSent(false);
    setIsStreaming(true);

    setRecent(prev => {
      const next = [question, ...prev.filter(q => q !== question)].slice(0, RECENT_MAX);
      saveRecent(next);
      return next;
    });

    try {
      const resp = await fetch(`${apiConfig.apiBaseUrl}/api/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          addressResolved,
          selectedDocument,
          conversationId: conversationIdRef.current,
        }),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        setError(errBody.error || `שגיאה (${resp.status})`);
        setIsStreaming(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) >= 0) {
          const chunk = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (!chunk.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(chunk.slice(6));
            if (event.type === 'delta') {
              setStreamingText(t => t + (event.text || ''));
            } else if (event.type === 'thinking') {
              // Internal scratch reasoning from intermediate rounds —
              // shown as a small "working on it" indicator, not the answer.
              setThinkingTrail(arr => [...arr, event.text || '']);
            } else if (event.type === 'tool_call') {
              setThinkingTrail(arr => [...arr, `→ ${event.name}`]);
            } else if (event.type === 'citation') {
              // Dedupe by id — same chunk can arrive twice if the LLM
              // calls search_plan_text both globally and plan-scoped.
              setCitations(cs => (cs.some(x => x.id === event.citation?.id) ? cs : [...cs, event.citation]));
            } else if (event.type === 'done' || event.type === 'final') {
              if (event.payload) {
                setToolsUsed(event.payload.tools_used || []);
                setConfidence(event.payload.confidence || null);
                // Always replace with the final answer (even if empty),
                // so we don't show stale streaming buffer.
                setStreamingText(event.payload.answer || '');
                // Canonical citation list from server (already deduped).
                if (Array.isArray(event.payload.citations)) {
                  setCitations(event.payload.citations);
                }
              }
              if (event.chat_query_id) setChatQueryId(event.chat_query_id);
            } else if (event.type === 'error') {
              setError(event.message || event.code || 'שגיאה בעיבוד התשובה');
            }
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setIsStreaming(false);
    }
  }, [question, addressResolved, selectedDocument, isStreaming]);

  const sendFeedback = useCallback(async (thumbsUp) => {
    if (!chatQueryId || feedbackSent) return;
    try {
      await fetch(`${apiConfig.apiBaseUrl}/api/ask-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_query_id: chatQueryId, thumbs_up: thumbsUp }),
      });
      setFeedbackSent(true);
    } catch { /* ignore */ }
  }, [chatQueryId, feedbackSent]);

  return (
    <div className="chat-assistant" dir="rtl">
      <h2>מאגר ידע — תכנון ובנייה</h2>
      <div className="corpus-row">
        <label htmlFor="kb-corpus-select">מקור ידע</label>
        <select
          id="kb-corpus-select"
          value={selectedDocumentId}
          onChange={e => setSelectedDocumentId(e.target.value)}
          disabled={isStreaming}
        >
          {DOCUMENT_OPTIONS.map(option => (
            <option key={option.id || 'planning'} value={option.id}>{option.title}</option>
          ))}
        </select>
        {selectedDocument && <span className="document-pill">מסמך פעיל: {selectedDocument.title}</span>}
      </div>
      {!selectedDocument && (
        <div className="address-row">
          <CustomAutocomplete
            onPlaceSelected={handleAddressSelected}
            placeholder="הקלד כתובת (לדוגמה: שלום עליכם 38)"
          />
          {addressResolved && (
            <span className="address-pill">
              ✓ {addressResolved.text} {addressResolved.parcel_key ? `(${addressResolved.parcel_key})` : ''}
            </span>
          )}
        </div>
      )}

      <div className="question-row">
        <textarea
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAsk(); } }}
          placeholder={selectedDocument ? 'שאל שאלה על המסמך שנבחר (למשל: מה הגוש והחלקה?)' : 'שאל שאלה על התכנון בכתובת זו (למשל: מה ניצול הקרקע המירבי?)'}
          rows={2}
        />
        <button onClick={handleAsk} disabled={isStreaming || !question.trim()}>
          {isStreaming ? 'מעבד...' : 'שאל'}
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {(streamingText || isStreaming) && (
        <div className="answer-area">
          {isStreaming && thinkingTrail.length > 0 && !streamingText && (
            <div className="thinking-indicator">
              <span className="spinner" /> מעבד… {thinkingTrail.slice(-1)[0] || ''}
            </div>
          )}
          <div className="answer-text">
            {streamingText ? renderRichText(streamingText) : (!isStreaming && 'לא התקבלה תשובה. נסה לנסח אחרת.')}
          </div>
          {confidence && (
            <div className={`confidence confidence-${confidence}`}>
              ביטחון: {confidence === 'high' ? 'גבוה' : confidence === 'medium' ? 'בינוני' : 'נמוך'}
              {toolsUsed.length > 0 && ` · כלים: ${toolsUsed.join(', ')}`}
            </div>
          )}
          {chatQueryId && !feedbackSent && (
            <div className="feedback">
              <button onClick={() => sendFeedback(true)}>👍</button>
              <button onClick={() => sendFeedback(false)}>👎</button>
            </div>
          )}
          {feedbackSent && <div className="feedback-thanks">תודה על המשוב!</div>}
        </div>
      )}

      {recent.length > 0 && (
        <aside className="recent-questions">
          <h3>שאלות אחרונות</h3>
          <ul>
            {recent.map((q, i) => (
              <li key={i}>
                <button className="recent-link" onClick={() => setQuestion(q)}>{q}</button>
              </li>
            ))}
          </ul>
        </aside>
      )}

      {citations.length > 0 && (
        <aside className="citations-panel">
          <h3>מקורות</h3>
          <ol>
            {citations.map((c, i) => {
              const kind = getCitationKind(c);
              const pages = formatCitationPages(c);
              const body = formatCitationBody(c);
              const note = formatCitationNote(c);
              const title = formatCitationTitle(c);
              return (
                <li key={c.id || i} className={`citation-item citation-${kind}`}>
                  <div className="citation-header">
                    <span className="citation-kind">{getCitationKindLabel(kind)}</span>
                    {c.pdf_url ? (
                      <a className="citation-title-link" href={c.pdf_url} target="_blank" rel="noreferrer">
                        {title}
                      </a>
                    ) : (
                      <strong>{title}</strong>
                    )}
                    {pages && <span className="citation-pages"> · {pages}</span>}
                    {c.approval_date && <span className="citation-pages"> · {c.approval_date}</span>}
                  </div>
                  {body && (
                    <blockquote className={kind === 'plan_metadata' ? 'citation-note' : 'excerpt'}>
                      {body}
                    </blockquote>
                  )}
                  {note && <div className="citation-note">{note}</div>}
                  {c.pdf_url && (
                    <a className="citation-open-link" href={c.pdf_url} target="_blank" rel="noreferrer">
                      פתח עמוד מקור
                    </a>
                  )}
                </li>
              );
            })}
          </ol>
        </aside>
      )}
    </div>
  );
}
